import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import isNil from 'lodash/isNil';
import startsWith from 'lodash/startsWith';
import TokenProvider from '../TokenProvider/TokenProvider';
import Cookies from '../Cookies/Cookies';
import LocalStorage from '../LocalStorage/LocalStorage';
import Constants, { THRESOLD_SAVEDITEMS } from '../../common/Constants';

/* istanbul ignore next */
export const replaceSubdomain = (url, preferences = {}) => {
    const marketingUrlChange = get(preferences, 'MarketingUrlChange', {});
    const {
        changeCloud = false,
        fromUrl = '',
        toUrl = '',
        changeS7 = false,
        imageUrlS7 = '',
        imageStageUrlS7 = '',
        contentStageUrlS7 = '',
        contentUrlS7 = '',
    } = marketingUrlChange;
    if (changeS7) {
        const isTestMktDomain = url.includes('test.mkt-jcpenney.com');
        const imageS7Types = ['jpg', 'jpeg', 'png'];
        const videoS7Types = ['mp4'];
        const imageQueryParams = '?scl=1&qlt=75';
        const urlSplit = url.split('/');
        const imageNameWithParam = urlSplit.pop();
        const imageNameWithParamSplit = imageNameWithParam.split('?');
        const imageName = imageNameWithParamSplit[0];
        const imageNameSplit = imageName.split('.');
        const imageExtension = imageNameSplit[1];
        let imageNameWithoutExtension = imageNameSplit[0];
        const isImageExtension = imageS7Types.indexOf(imageExtension) !== -1;
        const isVideoExtension = videoS7Types.indexOf(imageExtension) !== -1;
        if (imageName.length > 125) {
            const imageFirstName = imageNameWithoutExtension.substr(0, 75);
            const imageLastName = imageNameWithoutExtension.substr(
                imageNameWithoutExtension.length - 40
            );
            imageNameWithoutExtension = `${imageFirstName}${imageLastName}`;
        }

        if (isImageExtension && isTestMktDomain) {
            return `${imageStageUrlS7}${imageNameWithoutExtension}${imageQueryParams}`;
        }
        if (isImageExtension) {
            return `${imageUrlS7}${imageNameWithoutExtension}${imageQueryParams}`;
        }
        if (isVideoExtension && isTestMktDomain) {
            return `${contentStageUrlS7}${imageNameWithoutExtension}${imageQueryParams}`;
        }
        if (isVideoExtension) {
            return `${contentUrlS7}${imageNameWithoutExtension}${imageQueryParams}`;
        }

        return url;
    }
    if (changeCloud && url.indexOf(fromUrl) !== -1) {
        const supportFileTypes = ['.jpg', '.jpeg', '.gif', '.png'];
        let isSupportedFile = false;
        supportFileTypes.forEach((imageName) => {
            if (url.indexOf(imageName) !== -1) {
                isSupportedFile = true;
            }
        });
        if (isSupportedFile) {
            return url.replace(fromUrl, toUrl);
        }
    }
    return url;
};

/* istanbul ignore next */
export const checkUrlExtension = (url) => {
    if (url) {
        const urlSplit = url.split('/');
        const imageNameWithParam = urlSplit.pop();
        const imageNameWithParamSplit = imageNameWithParam.split('?');
        const imageName = imageNameWithParamSplit[0];
        return imageName.split('.')[1];
    }
    return url;
};

/**
 * This method returns the OS type from which the site is being accessed
 * @returns String OS Type
 */
/* istanbul ignore next */
export const findMobileOS = () => {
    let mobileOS = 'unknown';
    if (window && window.navigator) {
        const userAgent = navigator.userAgent || navigator.vendor || window.opera;
        // Windows Phone must come first because its UA also contains "Android"
        if (/windows phone/i.test(userAgent)) {
            mobileOS = 'Windows Phone';
        }

        if (/android/i.test(userAgent)) {
            mobileOS = 'Android';
        }

        // iOS detection from: http://stackoverflow.com/a/9039885/177710
        // add in detection for iPad iOS 13+
        if (
            (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) ||
            (userAgent?.includes('Mac') && 'ontouchend' in document)
        ) {
            mobileOS = 'iOS';
        }
    }
    return mobileOS;
};

/**
 * This method return the channel info by device type
 * @returns Channel Info (mobile, tablet, desktop) lowercase
 */
/* istanbul ignore next */
export const fetchChannelInfo = (deviceType) => {
    if (deviceType.isDesktop) {
        return 'desktop';
    }
    if (deviceType.isTablet) {
        return 'tablet';
    }
    return 'mobile';
};

/**
 *
 * Pass in a URL returns only the path
 * E.g. var x = Utils.getPathFromUrl('https://www.google.com/search?source=hp&q=shirts');
 * console.log(x); //prints /search?source=hp&q=shirts&oq=shirts
 * For some reason if the url cannot be parsed, return the original value as is
 * @param {any} url
 * @returns path
 */
export const getPathFromUrl = (url) => {
    const defaultPath = '/';
    if (!url) {
        return defaultPath;
    }

    if (url.indexOf('/') === 0) {
        // Starts with the / and return the path as is
        return url;
    }

    const matches = url.match(/^[^#]*?:\/\/.*?(\/.*)$/);
    if (matches && matches.length === 2) {
        return matches[1] || defaultPath;
    }
    return defaultPath;
};

/**
 * Check for savedItems and return true if the length of items is
 * less than 50 else returns false
 * @static
 * @param {any} object
 * @returns
 */
export const triggerAddSaveItems = (stateObj) => {
    const savedItems = stateObj.savedItems || {};
    const keys = Object.keys(savedItems);
    return keys.length < THRESOLD_SAVEDITEMS;
};

/**
 * Validates zipcode
 * zipcode should contains 5 numbers
 * @param {string}
 * @returns
 */
/* istanbul ignore next */
export const validateZipCode = (zipCode) => {
    const isZipCodeRegex = /^\d+$/;

    /* istanbul ignore else */
    if (isZipCodeRegex.test(zipCode) && zipCode.length !== 5) {
        return false;
    }
    return true;
};

/* This method will put logs in splunk on server side rendering
 * @param logger
 * @param request
 * @param cookie
 */
/* istanbul ignore next */
export const doPostSSRLogs = (logger, request, cookie, cluster) => {
    if (!logger) {
        return;
    }
    const logRequest = {};
    const response = request.response || {};
    logRequest.requestInfo = request.requestInfo;
    logRequest.responseCode = response.status;
    logRequest.traceId = response.headers && response.headers.get('x-trace-id');
    if (!isEmpty(cookie)) {
        logRequest.cookies = {
            DPOrder: cookie.DPOrder,
            DPJSESSIONID: cookie.DPJSESSIONID,
            DPInstance: cookie.DPInstance,
            DPCluster: cookie.DPCluster,
            DPCloudOrigin: cluster,
            DPCloudCluster: cluster,
        };
    }
    const errorInfo = Array.isArray(response.data) ? response.data[0] : response.data;
    if (errorInfo && errorInfo.errorCode) {
        logRequest.errorInfo = errorInfo;
    }
    logger.info(logRequest);
};

/**
 * util function to read URL params
 * @param {string} name - query parameter name
 * @param {http url} url - soruce of url to read params
 */
export const getURLParameterBykey = (name, url, removeSplChars) => {
    const newName = name.replace(/[[\]]/g, '\\$&');
    const regex = new RegExp(`[?&]${newName}(=([^&#]*)|&|#|$)`);
    let results = regex.exec(url);
    if (!results) return null;
    if (!results[2]) return '';
    results = decodeURIComponent(results[2]);
    return removeSplChars ? results.replace(/['+\s]/g, '') : results.replace(/\+/g, ' ');
};

/**
 * util function to read URL params
 * @param {string} key - query parameter name
 * @param {string} value - query parameter value
 * @param {http url} url - soruce of url to read params
 */
/* istanbul ignore next */
export const updateQueryStringParameter = (url, key, value) => {
    if (value && url) {
        const re = new RegExp(`([?&])${key}=.*?(&|$)`, 'i');
        const separator = url.indexOf('?') !== -1 ? '&' : '?';
        if (url.match(re)) {
            return url.replace(re, `$1${key}=${value}$2`);
        }
        return `${url}${separator}${key}=${value}`;
    }
    return url;
};

/* istanbul ignore next */
export const getFeatureflag = (flagName) => {
    const getContext =
        (!__SERVER__ && window.__PRELOADED_STATE__ && window.__PRELOADED_STATE__.context) || {};
    const featureFlags = getContext.featureFlags || {};
    return featureFlags && featureFlags[flagName];
};

export const isFeatureEnabled = (
    featureFlags,
    preferences,
    reqHeaders,
    featurePath,
    cookieName,
    headerName,
    preferencesPath
) => {
    // before going into OFF--> ON testing
    // We make sure that user is not making intentional ON --> OFF Tesing
    // pre-requisites 1

    if (
        headerName &&
        get(reqHeaders, String(headerName).toLowerCase(), '').trim().toLowerCase() === 'false'
    ) {
        return false;
    }

    // pre-requisites 2
    if (
        cookieName &&
        get(reqHeaders, `cookies.${cookieName}`, '').trim().toLowerCase() === 'false'
    ) {
        return false;
    }

    // OFF--> ON testing
    // first priority to check for feature flag
    if (featurePath && get(featureFlags, featurePath)) {
        return true;
    }
    // second priority to check for preferences if its very small part of enabled
    // component
    if (preferencesPath && get(preferences, preferencesPath)) {
        return true;
    }
    // third priority
    if (
        headerName &&
        get(reqHeaders, String(headerName).toLowerCase(), '').trim().toLowerCase() === 'true'
    ) {
        return true;
    }

    // last priority
    if (
        cookieName &&
        get(reqHeaders, `cookies.${cookieName}`, '').trim().toLowerCase() === 'true'
    ) {
        return true;
    }
    // Note: In case, you find empty headers: {} even you set correctly
    // it might be issue with whitelisting of cookies or isContextHeadersRequired flag
    // Please shout for help in YODA-COMMON in that case.

    // yeah if no flag present passed, or truthy
    // then return false to render func
    return false;
};

/**
 * util function to sync localstorage value with cookie in case
 * of cookie drops or becomes empty by any system and we hard depend
 * on cookie for any rendering or actions
 * @param {string} key - query parameter name
 * @param {string} retrieveWithKey - if localstorage is stored as differnt key
 */
export const syncAndGetCookieToken = (key, retrieveWithKey) => {
    if (isNil(key) || key === '') return null;
    const cookieValue = Cookies.load(key);
    if (!isNil(cookieValue)) return cookieValue;

    const localStorageValue = LocalStorage.getData(key, retrieveWithKey);

    if (isNil(localStorageValue)) return cookieValue;

    // cookie is dropped for some reason
    // lets sync
    Cookies.save(key, localStorageValue);
    return localStorageValue;
};

/* istanbul ignore next */
export const getValueFromUPS = (key, userPreferredStoreCookie) => {
    if (key && !isEmpty(userPreferredStoreCookie)) {
        let uspCookie = userPreferredStoreCookie;
        if (typeof userPreferredStoreCookie === 'string') {
            uspCookie = JSON.parse(userPreferredStoreCookie);
        }
        return get(uspCookie, key);
    }
    return '';
};

/**
 * Read zip code from User Preferred Store
 *
 */
/* istanbul ignore next */
export const getZipCodeFromUPS = (ssrCookie, locationServiceCookies) => {
    let userZipCode = '';
    if (ssrCookie) {
        const { userPreferredStore = '' } = locationServiceCookies;
        const userPreferredStoreCookie = ssrCookie.userPreferredStore;
        userZipCode =
            getValueFromUPS('inputZipCode', userPreferredStoreCookie) ||
            getValueFromUPS('zipcode', userPreferredStoreCookie) ||
            ssrCookie.ak_zip;
    } else {
        const userPreferredStore = Cookies.load(locationServiceCookies.userPreferredStore);
        userZipCode =
            getValueFromUPS('inputZipCode', userPreferredStore) ||
            getValueFromUPS('zipcode', userPreferredStore) ||
            Cookies.load('ak_zip');
    }
    return userZipCode;
};

/**
 * JIRA: MCYODA-18132 - To check User Zipcode from cookie & name as 'DP_USER_DEFAULT_ADDRESS_ZIP'
 * Cookie(DP_USER_DEFAULT_ADDRESS_ZIP) then read Akamai User Zipcode from Local Storage(ak_zip)
 * Read & return User zipcode & pass to Get Order Service call Header on Cart page
 */
/* istanbul ignore next */
export const getUserZipCode = (ssrCookie, useUserPreferStore = false, locationServiceCookies) => {
    let userZipCode = '';
    if (ssrCookie) {
        userZipCode = ssrCookie.DP_USER_DEFAULT_ADDRESS_ZIP;
        if (isEmpty(userZipCode) || userZipCode === 'null') {
            if (useUserPreferStore) {
                userZipCode = getZipCodeFromUPS(ssrCookie, locationServiceCookies);
            } else {
                userZipCode = ssrCookie.ak_zip;
            }
        }
    } else {
        userZipCode = TokenProvider.get('DP_USER_DEFAULT_ADDRESS_ZIP');
        if (isEmpty(userZipCode) || userZipCode === 'null') {
            if (useUserPreferStore) {
                userZipCode = getZipCodeFromUPS(ssrCookie, locationServiceCookies);
            } else {
                userZipCode = Cookies.load('ak_zip');
            }
        }
    }
    if (userZipCode && (userZipCode.length !== 5 || Number.isNaN(userZipCode))) {
        return '';
    }
    return userZipCode || '';
};

/**
 * DP_USER_STATE = 1 means login user and DP_USER_STATE = 2 means recognized user.
 */
export const isGuestUser = () => {
    return (
        TokenProvider.get(Constants.DP_USER_STATE) !== '1' &&
        TokenProvider.get(Constants.DP_USER_STATE) !== '2'
    );
};

/**
 * This method returns true if the browser is internnet explorer
 * @returns Boolean
 */
/* istanbul ignore next */
export const detectisIE = () => {
    let isIE = false;
    if (!__SERVER__ && window && window.navigator) {
        const { userAgent } = navigator;
        /* MSIE used to detect old browsers and Trident used to newer ones */
        isIE = userAgent.indexOf('MSIE ') > -1 || userAgent.indexOf('Trident/') > -1;
    }
    return isIE;
};

/**
 * This method returns true if the browser is microsoft edge
 * @returns Boolean
 */
/* istanbul ignore next */
export const detectisEdge = () => {
    let isEdge = false;
    if (!__SERVER__ && window && window.navigator) {
        const { userAgent } = navigator;
        isEdge = userAgent.indexOf('Edge') > -1;
    }
    return isEdge;
};

/**
 * This method returns true if the browser is microsoft edge
 * @returns Boolean
 */
/* istanbul ignore next */
export const detectisChrome = () => {
    let isChrome = false;
    if (!__SERVER__ && window && window.navigator) {
        const { userAgent } = navigator;
        isChrome = userAgent.indexOf('Chrome') > -1;
    }
    return isChrome;
};

/**
 * This method returns true if the browser is safari
 * @returns Boolean
 */
/* istanbul ignore next */
export const detectSafari = () => {
    let isSafari = false;
    if (!__SERVER__ && navigator?.vendor) {
        const { vendor } = navigator;
        isSafari = vendor.indexOf('Apple') > -1;
    }
    return isSafari;
};

/**
 * This method returns true if the browser is iOS Safari
 * @returns Boolean
 */
/* istanbul ignore next */
export const isIosSafari = () => {
    return !__SERVER__ && /iP(ad|hone|od).+Version\/[\d.]+.*Safari/i.test(navigator.userAgent);
};

/**
 * This method returns true if the OS is Windows
 * @static
 * @returns Boolean
 */
/* istanbul ignore next */
export const isWindowsDesktop = () => {
    return navigator.userAgent.includes('Windows');
};

/**
 * This method returns relative url if we provide absolute url
 * @static
 * @returns url
 */

export const getRelativeUrl = (url) => {
    const regEx = new RegExp('^([a-z]+://|//)', 'i');
    if (regEx.test(url) || startsWith(url, 'www') || startsWith(url, 'm.')) {
        const preparedUrl = startsWith(url, 'http') ? url : `https://${url}`;
        // absoluteUrl = new URL(url); return absoluteUrl.pathname + absoluteUrl.search;
        // IE is not support URL class so comments the above code and added below
        const urlPath = preparedUrl.replace(/^(?:https?:\/+)?(:?[^/#?]+)?/i, '');
        return urlPath.indexOf('/') === -1 ? `/${urlPath}` : urlPath;
    }
    return url;
};

/**
 * This method parses json and returns empty object if parsing throws exception
 * @returns object
 */
/* istanbul ignore next */
export const parseJson = (jsonString) => {
    try {
        return JSON.parse(jsonString);
    } catch (err) {
        return {};
    }
};

/**
 * Gets blackbox id from window object from iovation script / native app
 */
/* istanbul ignore next */
export const getBlackBoxId = (isNativeAppCheckout = false) => {
    let bbId = null;
    try {
        if (!__SERVER__) {
            // native app sets the value on this object,
            // would return null for web
            bbId = get(window, 'jcpWebApp.blackbox', null);
            if (isNativeAppCheckout) {
                // bbId = Cookies.load('DP_APP_BLACK_BOX', true) || null;
                /* reading it from document cookie, as cookies.load does not support
                    when cookie value has "=" */
                const documentCookie = document.cookie;
                const blackboxCookieArray = documentCookie.split('DP_APP_BLACK_BOX=');
                if (blackboxCookieArray.length > 1) {
                    const bbIdValue = blackboxCookieArray[1].split(';')[0] || null;
                    bbId = bbIdValue && decodeURIComponent(bbIdValue);
                }
            }
            if (isEmpty(bbId)) {
                bbId =
                    window.IGLOO &&
                    typeof window.IGLOO.getBlackbox === 'function' &&
                    window.IGLOO.getBlackbox().blackbox;
            }
        }
    } catch (err) {
        console.log(' Error in getting black box id', err);
    }
    return bbId;
};

export const isRevieveBannerIRIS = (analyticsTag) => {
    let revieveAnalyticsID = '';
    const isSkinCare = !!(analyticsTag?.toUpperCase().indexOf('REVIEVE_BANNER_SD') !== -1);
    const isVirtualTryOn = !!(analyticsTag?.toUpperCase().indexOf('REVIEVE_BANNER_VTO') !== -1);
    const isHairCare = !!(analyticsTag?.toUpperCase().indexOf('REVIEVE_BANNER_HC') !== -1);
    const isRevieve = isSkinCare || isVirtualTryOn || isHairCare;
    if (isRevieve) {
        if (isSkinCare) {
            revieveAnalyticsID = 'skincare';
        } else if (isVirtualTryOn) {
            revieveAnalyticsID = 'vto';
        } else if (isHairCare) {
            revieveAnalyticsID = 'haircare';
        }
    }
    return { isRevieve, revieveAnalyticsID };
};

export const isRevieveBannerCM = (analyticsTag) => {
    let revieveAnalyticsID = '';
    const isSkinCare = !!(analyticsTag?.toUpperCase().indexOf('REVIEVE-BANNER-SD') !== -1);
    const isVirtualTryOn = !!(analyticsTag?.toUpperCase().indexOf('REVIEVE-BANNER-VTO') !== -1);
    const isHairCare = !!(analyticsTag?.toUpperCase().indexOf('REVIEVE-BANNER-HC') !== -1);
    const isRevieve = isSkinCare || isVirtualTryOn || isHairCare;
    if (isRevieve) {
        if (isSkinCare) {
            revieveAnalyticsID = 'skincare';
        } else if (isVirtualTryOn) {
            revieveAnalyticsID = 'vto';
        } else if (isHairCare) {
            revieveAnalyticsID = 'haircare';
        }
    }
    return { isRevieve, revieveAnalyticsID };
};

// B2B Voucher Portal code //
export function isAgentLogged(b2bContext) {
    const enableB2BVoucher = getFeatureflag('enableB2BVoucher');
    let isAgent = false;
    const isAgentLoggedCookie = b2bContext?.DP_USER_ROLE || TokenProvider.get('DP_USER_ROLE', true);
    if (enableB2BVoucher && isAgentLoggedCookie) {
        isAgent = true;
    }
    return isAgent;
}

export function isSentToAgencyFromCart() {
    const enableB2BVoucher = getFeatureflag('enableB2BVoucher');
    let isSendToAgency = false;
    const isSentToAgencyCookie = TokenProvider.get('isSendToAgency', true) || false;
    if (enableB2BVoucher && isSentToAgencyCookie === 'true') {
        isSendToAgency = true;
    }
    return isSendToAgency;
}
